/**
 * `/tabelle` -- the Google connect, the Picker, and the landing both providers
 * share.
 *
 * DECIDES NOTHING ITSELF, and APPLIES nothing itself either. Every decision
 * (which state the binding is in, what a scope means, whether an identity was
 * actually read) and every mutation (`*Action`) lives in lib/sheetView.ts and
 * lib/oauthLanding.ts, where a Node test can hold the collaborators -- the
 * STANDING RULE from the Task 7-10 reviews. What is left here is JSX and
 * component state.
 *
 * TWO THINGS ARE RENDERED TOGETHER, ALWAYS, and that is deliberate:
 * `<ConnectOutcome>` (which paints nothing at all on an ordinary visit) and
 * the binding panel below it. The alternative -- the view branching on the
 * landing to decide which of the two to mount -- puts a classification
 * decision back into JSX, and it breaks the moment `clearSearch` empties the
 * query: the view would re-classify `none` one render later and erase the
 * screen it had just decided to show.
 *
 * THE CONNECT IS A TOP-LEVEL NAVIGATION, never a fetch (plan 3b amendment A1).
 * The whole argument is in lib/navigation.ts; this file must not acquire a
 * second way to leave the page.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAdminConfig, useMe } from '../Boot';
import { useRoute } from '../Router';
import { ADMIN_API } from '../lib/api';
import { guardedAction } from '../lib/gate';
import { fetchGoogleAccount } from '../lib/googleAccount';
import { pickSpreadsheetId } from '../lib/googlePicker';
import { t } from '../lib/i18n';
import {
  searchParamsFromLandingQuery, startProviderConnect, revocationLink,
} from '../lib/oauthLanding';
import {
  createSheetFromTemplateAction, finishGoogleConnect, pickAndConnectSheet,
  readGoogleIdentity, repairSheet, scopeLabels, sheetViewModel,
  type GoogleIdentityModel, type MutationShell, type SheetDetailModel,
} from '../lib/sheetView';
import { sheetQueryOptions } from '../queries';
import { ConnectOutcome } from '../components/ConnectOutcome';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageBody, PageHeader } from '../components/ui/page';
import { Spinner } from '../components/Spinner';
import { StatusPill } from '../components/StatusPill';
import type { GoogleFinish } from '../lib/types';

/** The panel-level busy/error pair every mutation-bearing panel shares, plus
 *  the re-entrancy gate every mutation-bearing panel now guards with -- the
 *  same shape views/MasterData.tsx and views/Staff.tsx use.
 *
 *  `gate` is a `useRef`, NOT the `busy` next to it, and the difference is the
 *  whole of final review I-2: `busy` is what the render shows, `gate` is what
 *  refuses the second click of a double-click before that render has happened.
 *  See lib/gate.ts for the argument and the controller's two-tier ruling. */
function useMutationShell(): MutationShell & {
  busy: boolean;
  error: string | null;
  gate: { current: string | null };
} {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gate = useRef<string | null>(null);
  return { queryClient, setBusy, setError, busy, error, gate };
}

function PanelError({ message }: { message: string | null }) {
  if (message === null) return null;
  return <p className="sheet-error" role="alert">{message}</p>;
}

/** The provider's own revocation page, rendered wherever an operator might
 *  decide this connection is not theirs. `rel` belts index.html's
 *  `referrer=same-origin`. */
function RevokeLink() {
  const link = revocationLink('google');
  return (
    <a href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>
  );
}

// ── the confirmation panel (plan 3c interface D13) ──────────────────────────

/**
 * WHICH ACCOUNT DID THIS CONNECTION LAND IN, and in which tenant?
 *
 * Rendered as `<ConnectOutcome>`'s success child, so it appears only after a
 * finish the server actually confirmed -- this panel never decides that a
 * connect succeeded.
 *
 * The identity is READ, not assumed: `readGoogleIdentity` mints a picker token
 * and asks Drive's about.get (lib/googleAccount.ts). If that fails, or answers
 * without naming anybody, the panel says so in the same place it would have
 * put the address. It still shows the scope, the tenant and the revocation
 * link, because those are the checks the operator can still make.
 */
function GoogleConfirm({ finish, tenantName }: { finish: GoogleFinish; tenantName: string }) {
  const [identity, setIdentity] = useState<GoogleIdentityModel>({ kind: 'pending' });
  const [dismissed, setDismissed] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    // Once per mount: StrictMode double-invokes effects, and each run would
    // mint a second access token for no reason.
    if (startedRef.current) return;
    startedRef.current = true;
    void readGoogleIdentity({
      pickerToken: ADMIN_API.pickerToken,
      readAccount: (accessToken) => fetchGoogleAccount(accessToken),
      setIdentity,
    });
  }, []);

  if (dismissed) return null;

  return (
    <section className="sheet-confirm" role="status">
      <h2>{t('sheet.confirm.title')}</h2>

      <dl>
        <dt>{t('sheet.confirm.accountLabel')}</dt>
        <dd>
          {identity.kind === 'pending' ? <Spinner /> : null}
          {identity.kind === 'unavailable' ? <span className="sheet-confirm-unknown">{identity.note}</span> : null}
          {identity.kind === 'known' ? (
            <>
              {identity.email === null ? null : <span className="sheet-confirm-email">{identity.email}</span>}
              {identity.displayName === null ? null : <span className="sheet-confirm-name">{identity.displayName}</span>}
            </>
          ) : null}
        </dd>

        <dt>{t('sheet.confirm.tenantLabel')}</dt>
        <dd>{tenantName}</dd>

        <dt>{t('sheet.confirm.scopeLabel')}</dt>
        <dd>
          <ul>
            {scopeLabels(finish.scope).map((label) => <li key={label}>{label}</li>)}
          </ul>
        </dd>
      </dl>

      <p className="sheet-confirm-check">{t('sheet.confirm.check')}</p>
      <p><RevokeLink /></p>
      <button type="button" onClick={() => setDismissed(true)}>{t('sheet.confirm.acknowledge')}</button>
    </section>
  );
}

// ── the four panels ─────────────────────────────────────────────────────────

function ConnectPanel({ shell, onConnect }: {
  shell: ReturnType<typeof useMutationShell>;
  onConnect: () => void;
}) {
  return (
    <div className="sheet-panel sheet-panel-connect">
      <h2>{t('sheet.connect.title')}</h2>
      <PanelError message={shell.error} />
      <p>{t('sheet.connect.body')}</p>
      {/* The consent copy states what is actually requested BEFORE the
          redirect: drive.file reaches the files the operator picks and
          nothing else in their Drive. */}
      <p>{t('sheet.connect.scopeNote')}</p>
      <button type="button" disabled={shell.busy} onClick={onConnect}>
        {t('sheet.connect.submit')}
      </button>
    </div>
  );
}

/** SP-Onlinehandel's legacy binding (admin spec 4.5, 9): the operator owns it,
 *  so no connect, no Picker and no repair control is rendered at all -- not
 *  disabled, absent. */
function ServiceAccountPanel({ detail }: { detail: SheetDetailModel }) {
  return (
    <div className="sheet-panel sheet-panel-service-account">
      <h2>{t('sheet.title')}</h2>
      <StatusPill kind="neutral" label={t('sheet.serviceAccount.pill')} />
      <p>{t('sheet.serviceAccount.note')}</p>
      <SheetDetailView detail={detail} />
    </div>
  );
}

function ChooseSheetPanel({ shell, onPick, onCreate, templateUnavailable, pickerBlocked, title, onTitleChange }: {
  shell: ReturnType<typeof useMutationShell>;
  onPick: () => void;
  onCreate: (event: FormEvent<HTMLFormElement>) => void;
  templateUnavailable: boolean;
  /** googlePickerAppId is null (Task 14 amendment B): rather than open a
   *  Picker dialog that can fail silently (drive.file has no manual
   *  spreadsheet-id fallback), this panel shows an honest blocked state and
   *  never renders the pick button at all. */
  pickerBlocked: boolean;
  title: string;
  onTitleChange: (value: string) => void;
}) {
  return (
    <div className="sheet-panel sheet-panel-choose">
      <h2>{t('sheet.choose.title')}</h2>
      <PanelError message={shell.error} />
      <p>{t('sheet.choose.body')}</p>
      {pickerBlocked ? (
        <div className="sheet-picker-unavailable" role="status">
          <h3>{t('sheet.choose.pickerUnavailableTitle')}</h3>
          <p>{t('sheet.choose.pickerUnavailableBody')}</p>
        </div>
      ) : (
        <button type="button" disabled={shell.busy} onClick={onPick}>{t('sheet.choose.pick')}</button>
      )}

      <form className="sheet-create" onSubmit={onCreate}>
        <h3>{t('sheet.create.title')}</h3>
        <label>
          {t('sheet.create.titleLabel')}
          <input
            type="text"
            value={title}
            disabled={shell.busy}
            onChange={(event) => onTitleChange(event.target.value)}
          />
        </label>
        <button type="submit" disabled={shell.busy}>{t('sheet.create.submit')}</button>
      </form>

      {/* A missing template is the truthful state of the platform (admin spec
          8, open point 3), not a fault the owner caused and not something a
          retry can fix -- so it is a named state, never an error toast. */}
      {templateUnavailable ? (
        <div className="sheet-template-unavailable" role="status">
          <h3>{t('sheet.template.unavailableTitle')}</h3>
          <p>{t('sheet.template.unavailableBody')}</p>
        </div>
      ) : null}
    </div>
  );
}

function SheetDetailView({ detail }: { detail: SheetDetailModel }) {
  return (
    <>
      <p className="sheet-id">
        <a href={detail.url} target="_blank" rel="noopener noreferrer">{detail.spreadsheetId}</a>
      </p>
      {detail.validatedLine === null ? null : <p className="sheet-validated">{detail.validatedLine}</p>}
      {detail.lastError === null ? null : (
        <p className="sheet-last-error" role="alert">
          {t('sheet.lastError', { error: detail.lastError })}
        </p>
      )}
      {detail.columns.length === 0 ? (
        <p>{t('sheet.columns.empty')}</p>
      ) : (
        <table className="sheet-columns">
          <thead>
            <tr>
              <th>{t('sheet.columns.name')}</th>
              <th>{t('sheet.columns.index')}</th>
            </tr>
          </thead>
          <tbody>
            {detail.columns.map((column) => (
              <tr key={column.name}>
                <td>{column.name}</td>
                <td>{column.index}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function ConnectedPanel({ shell, detail, createdTabs, onRepair }: {
  shell: ReturnType<typeof useMutationShell>;
  detail: SheetDetailModel;
  createdTabs: string[] | null;
  onRepair: () => void;
}) {
  return (
    <div className="sheet-panel sheet-panel-connected">
      <h2>{t('sheet.title')}</h2>
      <StatusPill
        kind={detail.lastError === null ? 'ok' : 'warn'}
        label={detail.lastError === null ? t('sheet.pill.ok') : t('sheet.pill.warn')}
      />
      <PanelError message={shell.error} />
      <SheetDetailView detail={detail} />
      <button type="button" disabled={shell.busy} onClick={onRepair}>{t('sheet.repair')}</button>
      {createdTabs === null || createdTabs.length === 0 ? null : (
        <p className="sheet-created-tabs" role="status">
          {t('sheet.repair.createdTabs', { tabs: createdTabs.join(', ') })}
        </p>
      )}
    </div>
  );
}

// ── the view ────────────────────────────────────────────────────────────────

export function Sheet() {
  const me = useMe();
  const config = useAdminConfig(); // already resolved by Boot; this is a cache read
  const pickerAppId = config.data?.googlePickerAppId ?? null;
  const { search, clearSearch } = useRoute();
  const shell = useMutationShell();
  const sheet = useQuery(sheetQueryOptions());
  const [templateUnavailable, setTemplateUnavailable] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [createdTabs, setCreatedTabs] = useState<string[] | null>(null);

  // All four are TIER 1 under the lib/gate.ts ruling -- each one either mints a
  // credential, creates a document in the operator's Drive, or writes tabs into
  // a live spreadsheet -- so all four are guarded by the ref, never by
  // `shell.busy`. `handlePick` is the clearest case: its FIRST step is a picker
  // token, so a swallowed second click would mint two access tokens and open
  // two dialogs, neither of which a `disabled` attribute can prevent.
  function handleConnect() {
    guardedAction(shell.gate, 'googleStart', () => startProviderConnect({
      start: ADMIN_API.googleStart,
      setBusy: shell.setBusy,
      setError: shell.setError,
      queryClient: shell.queryClient,
    }));
  }

  function handlePick() {
    // Only reachable while pickerAppId is set: ChooseSheetPanel does not
    // render the pick button otherwise (Task 14 amendment B).
    guardedAction(shell.gate, 'connectSheet', () => pickAndConnectSheet({
      ...shell,
      pickerToken: ADMIN_API.pickerToken,
      pick: (accessToken) => pickSpreadsheetId(accessToken, t('sheet.choose.pickerTitle'), pickerAppId ?? undefined),
      connectSheet: ADMIN_API.connectSheet,
      onConnected: () => setCreatedTabs(null),
    }));
  }

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    guardedAction(shell.gate, 'createSheetFromTemplate', () => createSheetFromTemplateAction(
      newTitle,
      { ...shell, createSheetFromTemplate: ADMIN_API.createSheetFromTemplate, setTemplateUnavailable },
    ));
  }

  function handleRepair() {
    guardedAction(shell.gate, 'validateSheet', () => repairSheet({
      ...shell, validateSheet: ADMIN_API.validateSheet, onValidated: setCreatedTabs,
    }));
  }

  /** A plain call, not a nested `<SheetPanel />` component: a component
   *  DECLARED inside a render is a new type on every render, and React would
   *  remount the panel -- and drop its input state -- on every keystroke. */
  function renderPanel(): ReactNode {
    if (sheet.isPending) return <Spinner />;
    if (sheet.isError) {
      return (
        <div className="sheet-panel">
          <ErrorNotice error={sheet.error} />
          <button type="button" onClick={() => void sheet.refetch()}>{t('blocking.retry')}</button>
        </div>
      );
    }
    const model = sheetViewModel(sheet.data.sheet);
    switch (model.kind) {
      case 'notConnected':
        return <ConnectPanel shell={shell} onConnect={handleConnect} />;
      case 'serviceAccount':
        return <ServiceAccountPanel detail={model.detail} />;
      case 'noSheet':
        return (
          <ChooseSheetPanel
            shell={shell}
            onPick={handlePick}
            onCreate={handleCreate}
            templateUnavailable={templateUnavailable}
            pickerBlocked={pickerAppId === null}
            title={newTitle}
            onTitleChange={setNewTitle}
          />
        );
      case 'connected':
        return (
          <ConnectedPanel
            shell={shell}
            detail={model.detail}
            createdTabs={createdTabs}
            onRepair={handleRepair}
          />
        );
    }
  }

  // Boot resolves `me` before any view mounts, so this is a cache read.
  if (!me.isSuccess) return <Spinner />;
  const tenantName = me.data.tenant.name;

  return (
    <section className="sheet-view">
      <PageBody>
      <PageHeader title={t('nav.sheet')} />

      <ConnectOutcome<GoogleFinish>
        provider="google"
        search={searchParamsFromLandingQuery(search)}
        clearSearch={clearSearch}
        onRetry={handleConnect}
        onFinish={(state) => finishGoogleConnect(state, {
          queryClient: shell.queryClient,
          googleFinish: ADMIN_API.googleFinish,
        })}
      >
        {(result) => <GoogleConfirm finish={result} tenantName={tenantName} />}
      </ConnectOutcome>

      {renderPanel()}
      </PageBody>
    </section>
  );
}
